import { PMDataTable } from "../../utils/PMTableView/PMDataTable";
import { CellLink } from "../../utils/PMTableView/CellLink";
import { ItemStatus, dateToString } from "../../Utils";

const responsibleLabel = (item) =>
  item?.responsible_participant?.user?.displayTitle ||
  item?.responsible_participant?.user?.name ||
  "—";

const executorLabel = (item) =>
  item?.responsible_executor_participant?.user?.displayTitle ||
  item?.responsible_executor_participant?.user?.name ||
  "—";

// Full column definitions (with cell renderers + grouping). Exported so the
// list page can derive column metadata for the toolbar / useTableView hook.
export const getWorkOrderColumns = (t) => [
  {
    key: "number",
    label: t("Work Order #"),
    sortKey: "number",
    width: 110,
    always: true,
    cell: (item) => (
      <CellLink
        to={item?.id ? `/dashboard/work_orders/${item.id}` : null}
        className="text-[13.5px] font-semibold tabular-nums text-blue-600"
      >
        {item?.number ?? "—"}
      </CellLink>
    ),
  },
  {
    key: "title",
    label: t("Title"),
    sortKey: "title",
    width: 260,
    cell: (item) => (
      <span className="block max-w-[260px] truncate text-[13.5px] font-medium text-slate-800">
        {item?.title || item?.displayTitle || t("work_orders.table_no_title")}
      </span>
    ),
  },
  {
    key: "status",
    label: t("status"),
    sortKey: "status",
    width: 130,
    stopRowClick: true,
    groupable: true,
    groupValue: (item) => item?.status || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) => <ItemStatus item={item} />,
  },
  {
    key: "customer",
    label: t("customer"),
    width: 180,
    groupable: true,
    groupValue: (item) =>
      item?.customer?.displayTitle || item?.customer?.name || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) => (
      <CellLink
        to={item?.customer?.id ? `/dashboard/customers/${item.customer.id}` : null}
        className="block max-w-[180px] truncate text-[13px] text-slate-600"
      >
        {item?.customer?.displayTitle || item?.customer?.name || "—"}
      </CellLink>
    ),
  },
  {
    key: "project",
    label: t("Project"),
    width: 180,
    groupable: true,
    groupValue: (item) =>
      item?.project?.displayTitle || item?.project?.name || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) => (
      <span className="block max-w-[180px] truncate text-[13px] text-slate-600">
        {item?.project?.displayTitle || item?.project?.name || "—"}
      </span>
    ),
  },
  {
    key: "responsible",
    label: t("Responsible"),
    width: 150,
    cell: (item) => (
      <span className="block max-w-[150px] truncate text-[13px] text-slate-600">
        {responsibleLabel(item)}
      </span>
    ),
  },
  {
    key: "executor",
    label: t("Executor"),
    width: 150,
    cell: (item) => (
      <span className="block max-w-[150px] truncate text-[13px] text-slate-600">
        {executorLabel(item)}
      </span>
    ),
  },
  {
    key: "order_date",
    label: t("Order Date"),
    sortKey: "order_date",
    width: 120,
    defaultVisible: false,
    cell: (item) => (
      <span className="text-[13px] tabular-nums text-slate-500">
        {item?.order_date ? dateToString(item.order_date) : "—"}
      </span>
    ),
  },
  {
    key: "due_date",
    label: t("Due Date"),
    sortKey: "due_date",
    width: 120,
    cell: (item) => (
      <span className="text-[13px] tabular-nums text-slate-500">
        {item?.due_date ? dateToString(item.due_date) : "—"}
      </span>
    ),
  },
];

export const WorkOrdersDataTable = ({
  data = [],
  isLoading = false,
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect = () => {},
  onRowClick,
  layout = "table",
  hideSelection = false,
}) => (
  <PMDataTable
    columns={columns}
    visibleKeys={visibleKeys}
    widths={widths}
    onResize={onResize}
    groupBy={groupBy}
    sort={sort}
    onSort={onSort}
    data={data}
    isLoading={isLoading}
    density={density}
    hasMore={hasMore}
    onLoadMore={onLoadMore}
    onSelectionChange={onItemsSelect}
    onRowClick={onRowClick}
    layout={layout}
    hideSelection={hideSelection}
  />
);

export { responsibleLabel, executorLabel };

export default WorkOrdersDataTable;
